import { type SkuId } from '@bluebrick/discounts-service-module'
import { isFinite } from '@bluebrick/saas-ui'
import { groupBy } from 'lodash-es'
import { DateTime } from 'luxon'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { now } from '@/common/dateUtils'
import { hasOverlap, isOpportunityEvent } from '@/common/utils'
import { useEventDetailsBaseStore } from '@/features/eventDetails/useEventDetailsBaseStore'
import { useEventDetailsDurationStore } from '@/features/eventDetails/useEventDetailsDurationStore'
import { useEventDetailsProducts } from '@/features/eventDetails/useEventDetailsProducts'
import { useEventDetailsRemoteDataStore } from '@/features/eventDetails/useEventDetailsRemoteDataStore'
import { useEventDetailsSourceEvent } from '@/features/eventDetails/useEventDetailsSourceEvent'
import { useEventDetailsStoreData } from '@/features/eventDetails/useEventDetailsStoreData'
import { type RecommendedDates, type EventData, type WithDates } from '@/types'

export const useEventDetailsDatesStore = defineStore('eventDetailsDates', () => {
	const { startDate, endDate, storeId, mode, sourceEvent } = storeToRefs(useEventDetailsBaseStore())
	const { updateStartDate, updateEndDate, updateEventLength } = useEventDetailsBaseStore()
	const { isFromStoreOpportunity, isFromMajorStoreOpportunity } = useEventDetailsSourceEvent()
	const { products } = useEventDetailsProducts()
	const { recommendedDates: recommendedRemoteDates, eventDurations, discountConfig } = storeToRefs(useEventDetailsRemoteDataStore())
	const { optimalDurationInDays } = storeToRefs(useEventDetailsDurationStore())
	const { canCalculateEventParameters, storeData, selectedSkuIds } = useEventDetailsStoreData()

	const recommendedStartDate = ref<DateTime | null>()
	const recommendedEndDate = ref<DateTime | null>()
	const recommendedDates = ref(new Map<SkuId, RecommendedDates>())

	const eventDatesDraft = computed<WithDates>(() => ({
		start_date: startDate.value,
		end_date: endDate.value
	}))

	const isEventInThePast = computed(() => {
		return !!startDate.value && startDate.value < now()
	})

	/**
	 * Updates recommended start date to be the start of a shortest common period for all selected
	 * SKUs according to algorithm described below. Recommended start date will be empty if we don't
	 * want to show any recommendations, for example if:
	 * - there is no recommendation available/possible
	 * - current start of the event already matches the recommendation
	 */
	function updateRecommendedStartDate() {
		if (isFromStoreOpportunity.value) {
			// Store opportunities have fixed dates so we don't want to show any recommendations for them
			recommendedStartDate.value = null

			return
		}

		// I've no idea how to type it so that `maximalStartDate` does not become `never` later on
		let maximalStartDate: DateTime | undefined = undefined as unknown as DateTime

		// Recommended dates is a per-SKU map
		;[...recommendedDates.value.values()].forEach((dates) => {
			if (dates.start_date?.isValid && (!maximalStartDate || dates.start_date > maximalStartDate)) {
				// Select the maximum start date from the list of start date recommendations for all involved SKUs
				maximalStartDate = dates.start_date
			}
		})

		if (!maximalStartDate?.isValid) {
			// No recommendation
			recommendedStartDate.value = null

			return
		}

		if (!startDate.value?.isValid) {
			// No current start date so we use the recommendation
			recommendedStartDate.value = maximalStartDate

			return
		}

		// Both dates are valid (current and recommendation) - return recommended date only if it differs from current one
		recommendedStartDate.value = maximalStartDate.toUTC().equals(startDate.value.toUTC()) ? null : maximalStartDate
	}

	/**
	 * Updates recommended end date to be the end of a shortest common period for all selected
	 * SKUs according to algorithm described below. Recommended end date will be empty if we don't
	 * want to show any recommendations, for example if:
	 * - there is no recommendation available/possible
	 * - current end of the event already matches the recommendation
	 */
	function updateRecommendedEndDate() {
		if (isFromStoreOpportunity.value) {
			// Store opportunities have fixed dates so we don't want to show any recommendations for them
			recommendedEndDate.value = null

			return
		}

		// I've no idea how to type it so that `minimalEndDate` does not become `never` later on
		let minimalEndDate: DateTime | undefined = undefined as unknown as DateTime

		products.value.forEach(({ uniqueSkuId }) => {
			// List of dates from which we'll select the minimal one and treat it as a recommended
			// end date
			const datesInMillisecondsToCheck: number[] = []
			const recommendedDatesForSKU = recommendedDates.value.get(uniqueSkuId)

			const optimalEventDurationForSKU = eventDurations.value.get(uniqueSkuId)

			// Maximum end date of current event
			const maxEndDateForSKU =
				isFinite(optimalEventDurationForSKU) && optimalEventDurationForSKU > 0 && startDate.value?.isValid
					? startDate.value.plus({ days: optimalEventDurationForSKU })
					: null

			if (maxEndDateForSKU?.isValid) {
				datesInMillisecondsToCheck.push(maxEndDateForSKU.toMillis())
			}

			if (recommendedDatesForSKU?.end_date?.isValid) {
				datesInMillisecondsToCheck.push(recommendedDatesForSKU?.end_date.toMillis())
			}

			if (minimalEndDate?.isValid) {
				datesInMillisecondsToCheck.push(minimalEndDate.toMillis())
			}

			if (datesInMillisecondsToCheck.length) {
				minimalEndDate = DateTime.fromMillis(Math.min.apply(null, datesInMillisecondsToCheck)).toUTC()
			}
		})

		if (!minimalEndDate?.isValid) {
			// No recommendation
			recommendedEndDate.value = null

			return
		}

		if (!endDate.value?.isValid) {
			// No current end date so we use the recommendation
			recommendedEndDate.value = minimalEndDate

			return
		}

		// Both dates are valid (current and recommendation) - return recommended date only if it differs from current one
		recommendedEndDate.value = minimalEndDate.toUTC().equals(endDate.value.toUTC()) ? null : minimalEndDate
	}

	/*
	 * General algorithm for finding recommended dates of current event:
	 * 1. take from API max length values for all involved SKUs
	 * 2. take from API recommended start/end dates for all involved SKUs
	 * 3. for start date:
	 *    3.1. if there is no recommendation from step 2 then we have "no start date recommendation"
	 *    3.2. if there is a recommendation from step 2 but it's the same as current start date then we have "no start date recommendation"
	 *    3.3. if there is a recommendation from step 2 and it's different than current start date then we use it as start date recommendation
	 * 4. for end date:
	 *    4.1. take all valid recommended end dates from step 2 and all valid computed end dates
	 *         (per SKU, based on current length and max length)
	 *    4.1. find a minimal date from the list from step 4.1.
	 *    4.2. if the list is empty then we have "no end date recommendation"
	 *    4.3. if there is a valid minimal date from step 4.1 but it's the same as current end date then we have "no end date recommendation"
	 *    4.4. if there is a valid minimal date from step 4.1 and it's different than current end date then we use it as end date recommendation
	 *
	 * In other words - if the recommendations per SKU are:
	 * - skuA:                [             ]
	 * - skuB:        [                 ]
	 * - skuC:             [              ]
	 * Then final recommendation will be:
	 *                        [         ]
	 * (the smallest intersection of recommended dates) to satisfy all SKUs/products selected in the side panel.
	 *
	 * In case we also have a max length limit for given SKU(s), we need to incorporate that information:
	 * event start date:    S
	 * - skuA:                [           M ]
	 * - skuB:        [                 ]        M
	 * - skuC:             [         M    ]
	 * where `S` is the start date of current event and `M` is maximum end date for given SKU based on max length
	 * In this case final recommendation will be:
	 *                        [      ]
	 *
	 * NOTE: Above "graphs" make sense only when read using a monotype font. If you're using a non-monotype
	 * font to read code then please don't do it. It's bad for your health.
	 */
	function updateRecommendedDates() {
		if (
			// No point in getting dates recommendation if:
			!canCalculateEventParameters.value || // store does not support calculated values
			isFromStoreOpportunity.value || // current event is created from store opportunity (in such case dates are fixed)
			!startDate.value?.isValid || // required input not defined or valid
			!endDate.value?.isValid || // required input not defined or valid
			!storeId.value || // required input not defined or valid
			(isFromMajorStoreOpportunity.value && mode.value !== 'create') // we're updating a major store event (in such case dates are fixed)
		) {
			return
		}

		recommendedDates.value.clear()

		const opportunities: EventData[] = []

		// Iterate over all events for products selected in the sidebar and keep opportunities
		storeData.value?.skus
			?.filter((sku) => selectedSkuIds.value.includes(sku.unique_sku_id))
			.forEach((sku) => {
				sku.events.forEach((event) => {
					if (isOpportunityEvent(event)) {
						opportunities.push(event)
					}
				})
			})

		// In case of newly created event first check if there is an opportunity that current dates overlap with
		// If there is then use that opportunity's dates as recommendation
		if (!sourceEvent.value?.id) {
			const opportunitiesBySKU = groupBy(opportunities, 'unique_sku_id')

			Object.entries(opportunitiesBySKU).forEach(([skuId, opportunitiesForSKU]) => {
				const matchingOpportunity = opportunitiesForSKU.find((opportunity) => hasOverlap(opportunity, eventDatesDraft.value))

				if (matchingOpportunity) {
					recommendedDates.value.set(skuId, {
						start_date: matchingOpportunity.start_date,
						end_date: matchingOpportunity.end_date
					})
				}
			})
		}

		if (mode.value === 'create') {
			// if a new event is added, we already have opportunities surrounding the created event
			updateRecommendedStartDate()
			updateRecommendedEndDate()

			return
		}

		if (recommendedRemoteDates.value) {
			for (const [skuId, recommendation] of recommendedRemoteDates.value.entries()) {
				recommendedDates.value.set(skuId, recommendation)
			}
		}

		updateRecommendedStartDate()
		updateRecommendedEndDate()
	}

	/**
	 * Sets specific start and end dates for the event. If end date is not given and optimal length
	 * of event is know then sets that length. Otherwise just sets event length to length from
	 * discount config.
	 */
	function updateEventTimes(startDate: DateTime, endDate: DateTime | undefined) {
		if (!storeId.value) {
			return
		}

		updateStartDate(startDate)

		if (endDate?.isValid) {
			updateEndDate(endDate)
		} else if (isFinite(optimalDurationInDays.value)) {
			updateEventLength(optimalDurationInDays.value)
		} else {
			updateEventLength(discountConfig.value.optimalLengthInDays)
		}
	}

	/**
	 * If there is an optimal duration for this event then keep current start date and update end
	 * date to match optimal duration. Otherwise it uses the default optimal duration for current store
	 */
	function setOptimalDuration() {
		if (optimalDurationInDays.value) {
			updateEventLength(optimalDurationInDays.value)
		} else {
			updateEventLength(discountConfig.value.optimalLengthInDays)
		}
	}

	function reset() {
		recommendedStartDate.value = null
		recommendedEndDate.value = null
	}

	return {
		recommendedStartDate,
		recommendedEndDate,
		isEventInThePast,
		eventDatesDraft,
		reset,
		updateRecommendedDates,
		updateEventTimes,
		setOptimalDuration
	}
})
